import type { Action, Match, Tournament } from '@airmail/engine';
import { t } from '@/i18n';
import { fmtTime } from './format';

/**
 * Aktionen in einem Satz beschreiben — für die Liste verworfener Aktionen
 * (docs/notes/konflikte.md). „match.result" sagt niemandem etwas; „Ergebnis
 * Bag Brothers – Cornholio 21:17" schon.
 *
 * Absichtlich grob: Es geht darum, die eigene Eingabe wiederzuerkennen, nicht
 * darum, jede Aktion vollständig auszuschreiben. Was hier fehlt, landet beim
 * Aktionstyp — besser als eine Lücke.
 */

function matchOf(tournament: Tournament, matchId: string): Match | undefined {
  for (const comp of tournament.competitions)
    for (const phase of comp.phases) {
      const m = phase.matches.find((x) => x.id === matchId);
      if (m) return m;
    }
  return undefined;
}

/** „Bag Brothers – Cornholio" (oder die Spielnummer, wenn die Teams noch offen sind) */
function matchLabel(tournament: Tournament, matchId: string): string {
  const m = matchOf(tournament, matchId);
  if (!m) return t('Spiel {id}', { id: matchId });
  const comp = tournament.competitions.find((c) => c.phases.some((p) => p.matches.some((x) => x.id === matchId)));
  const einer = (id: string): string => comp?.teams.find((x) => x.id === id)?.name ?? '?';
  const name = (slot: Match['a']): string => (slot.kind === 'team' ? (slot.partnerId ? `${einer(slot.teamId)} / ${einer(slot.partnerId)}` : einer(slot.teamId)) : '?');
  const teams = `${name(m.a)} – ${name(m.b)}`;
  return teams === '? – ?' ? t('Spiel {n}', { n: m.number ?? m.id }) : teams;
}

function courtName(tournament: Tournament, courtId: string): string {
  return tournament.courts.find((c) => c.id === courtId)?.name ?? courtId;
}

/** Ergebnis als „21:17" aus den Sätzen */
function score(action: Extract<Action, { type: 'match.result' }>): string {
  const sets = action.result.sets;
  return sets.map((s) => `${s.a}:${s.b}`).join(', ');
}

/** Worum es geht: das Spiel, das Feld, das Team — die Überschrift über beiden Ständen */
export function subjectOf(action: Action, tournament: Tournament): string {
  if ('matchId' in action && typeof action.matchId === 'string') return matchLabel(tournament, action.matchId);
  if ('courtId' in action && typeof action.courtId === 'string') return courtName(tournament, action.courtId);
  if ('teamId' in action && typeof action.teamId === 'string') return tournament.competitions.flatMap((c) => c.teams).find((x) => x.id === action.teamId)?.name ?? t('Team');
  if (action.type === 'court.add') return action.name;
  if (action.type === 'team.add') return action.team.name ?? t('Team');
  if ('phaseId' in action && typeof action.phaseId === 'string') return t('Spielphase');
  return t('Turnier');
}

/**
 * Wie es jetzt wirklich steht — die Gegenseite zur eigenen, verworfenen
 * Eingabe. Damit stehen beide Stände nebeneinander und die Leitung kann
 * entscheiden, welcher gilt (docs/notes/konflikte.md).
 */
export function currentStateOf(action: Action, tournament: Tournament): string {
  if ('matchId' in action && typeof action.matchId === 'string') {
    const m = matchOf(tournament, action.matchId);
    if (!m) return t('Dieses Spiel gibt es nicht mehr.');
    const wo = m.courtId ? t('auf {feld}', { feld: courtName(tournament, m.courtId) }) : '';
    switch (m.status) {
      case 'finished':
      case 'walkover': {
        const sets = m.result?.sets.map((x) => `${x.a}:${x.b}`).join(', ') ?? '';
        return t('Gewertet: {satz}', { satz: sets || t('kampflos') });
      }
      case 'running':
        return [t('Läuft gerade'), wo, m.startedAt ? t('seit {zeit}', { zeit: fmtTime(m.startedAt) }) : ''].filter(Boolean).join(' ');
      case 'cancelled':
        return t('Gestrichen');
      default:
        return t('Wartet noch');
    }
  }
  if ('courtId' in action && typeof action.courtId === 'string') {
    const c = tournament.courts.find((x) => x.id === action.courtId);
    if (!c) return t('Dieses Feld gibt es nicht mehr.');
    return c.active ? t('{name} ist aktiv', { name: c.name }) : t('{name} ist abgeschaltet', { name: c.name });
  }
  if ('teamId' in action && typeof action.teamId === 'string') {
    const team = tournament.competitions.flatMap((c) => c.teams).find((x) => x.id === action.teamId);
    if (!team) return t('Dieses Team gibt es nicht mehr.');
    return team.status === 'withdrawn' ? t('{name} ist zurückgezogen', { name: team.name }) : t('{name} spielt mit', { name: team.name });
  }
  if ('phaseId' in action && typeof action.phaseId === 'string') {
    const phase = tournament.competitions.flatMap((c) => c.phases).find((p) => p.id === action.phaseId);
    if (!phase) return t('Diese Phase gibt es nicht mehr.');
    return t('Phase steht auf „{status}"', { status: phase.status });
  }
  return t('Der Stand hat sich geändert.');
}

export function describeAction(action: Action, tournament: Tournament): string {
  switch (action.type) {
    case 'match.result':
      return t('Ergebnis {spiel}: {satz}', { spiel: matchLabel(tournament, action.matchId), satz: score(action) });
    case 'match.walkover':
      return t('Kampflos gewertet: {spiel}', { spiel: matchLabel(tournament, action.matchId) });
    case 'match.start':
      return t('Spiel gestartet: {spiel} auf {feld}', { spiel: matchLabel(tournament, action.matchId), feld: courtName(tournament, action.courtId) });
    case 'match.score':
      return t('Zwischenstand {spiel}', { spiel: matchLabel(tournament, action.matchId) });
    case 'match.reopen':
      return t('Spiel wieder geöffnet: {spiel}', { spiel: matchLabel(tournament, action.matchId) });
    case 'match.abort':
      return t('Spiel abgebrochen: {spiel}', { spiel: matchLabel(tournament, action.matchId) });
    case 'match.cancel':
      return t('Spiel gestrichen: {spiel}', { spiel: matchLabel(tournament, action.matchId) });
    case 'match.setPriority':
      return t('Reihenfolge geändert: {spiel}', { spiel: matchLabel(tournament, action.matchId) });
    case 'match.order':
      return t('Warteschlange neu geordnet');
    case 'team.add':
      return t('Team angelegt: {name}', { name: action.team.name ?? '?' });
    case 'team.update':
      return t('Team geändert: {name}', { name: action.patch.name ?? tournament.competitions.flatMap((c) => c.teams).find((x) => x.id === action.teamId)?.name ?? '?' });
    case 'team.withdraw':
      return t('Team zurückgezogen');
    case 'team.remove':
      return t('Team gelöscht');
    case 'team.setPlayers':
      return t('Spieler geändert: {name}', { name: tournament.competitions.flatMap((c) => c.teams).find((x) => x.id === action.teamId)?.name ?? '?' });
    case 'court.add':
      return t('Feld angelegt: {name}', { name: action.name });
    case 'court.update':
      return t('Feld geändert: {name}', { name: courtName(tournament, action.courtId) });
    case 'court.remove':
      return t('Feld gelöscht: {name}', { name: courtName(tournament, action.courtId) });
    case 'phase.draw':
      return t('Ausgelost');
    case 'phase.freeze':
      return t('Auslosung eingefroren');
    case 'phase.finish':
      return t('Phase abgeschlossen');
    case 'phase.moveTeam':
      return t('Team in eine andere Gruppe verschoben');
    case 'phase.setOptions':
      return t('Einstellungen der Phase geändert');
    case 'phase.updateConfig':
      return t('Modus geändert');
    case 'ko.startRound':
      return t('KO-Runde gestartet ({n} Teams)', { n: action.teamIds.length });
    case 'group.setCourtGroup':
      return t('Gruppe einem Spielort zugeordnet');
    case 'group.resolveLot':
      return t('Gleichstand ausgelost');
    case 'tournament.update':
      return t('Turnierdaten geändert');
    case 'tournament.pause':
      return t('Turnier angehalten oder fortgesetzt');
    case 'competition.updateRules':
      return t('Regeln geändert');
    default:
      return action.type;
  }
}
